/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_TRANSPARENT,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_PRIMARY,
  Button,
  DXP_DATA_ID_FORM_ERROR,
  DialogContentProps,
  ErrorMessage,
  ICON_ADD_CIRCLE_OUTLINE,
  ICON_REMOVE_CIRCLE,
  SelectField,
  SelectFieldOption,
  SelectFieldProps,
  TOAST_TYPE_DANGER,
  TOAST_TYPE_SUCCESS,
  Toast,
  ToastProps,
} from '@squiz/sds';
import { CDP_STRINGS } from 'apps/customer-data-platform/src/Constants/Strings.constants';
import { EntitySegment, getSegmentationService } from 'apps/customer-data-platform/src/Services/segmentation';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import difference from 'lodash/difference';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import property from 'lodash/property';
import uniq from 'lodash/uniq';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { SEGMENTATION_DATA_IDS } from '../../../../Segmentation.constants';
import { SuccessMetricDialogFormProps } from './SuccessMetricDialogForm.props';

/**
 * The dialog form for adding and editing success metrics for a segment.
 */
export const SuccessMetricDialogForm = (props: DialogContentProps): JSX.Element => {
  const { t } = useTranslation();
  const { close, customProps, setAfterClose, setButtonKeys, setButtons } = props;

  // CDP props
  const { eventList, resetSegments, selectedSegment, segmentList } = customProps as SuccessMetricDialogFormProps;

  const { updateActiveToast } = useToastStore();
  const segmentationService = getSegmentationService();

  const [formError, setFormError] = useState('');
  const [isFormSubmitted, setSubmit] = useState(false);
  const [selected] = useState<EntitySegment | null>(selectedSegment);
  const [selectedMetrics, setSelectedMetrics] = useState<Array<string | null>>(
    Object.keys(selectedSegment?.success || {}),
  );
  const isEditing =
    property(`success`)(selectedSegment) !== undefined &&
    Object.keys(property(`success`)(selectedSegment) as object).length >= 1;

  const availableMetrics = useMemo((): Array<string | null> => {
    if (eventList !== null) {
      const allEvents = Object.keys(eventList);
      return difference(allEvents, selectedMetrics);
    }
    return [];
  }, [eventList, selectedMetrics]);

  // Labels
  const labels = useMemo(() => {
    return {
      add: t(CDP_STRINGS.add),
      addEventFeed: t(CDP_STRINGS.addEventFeed),
      addSuccessMetric: t(CDP_STRINGS.addSuccessMetric),
      addSuccessMetricDescription: t(CDP_STRINGS.addSuccessMetricDescription),
      cancel: t(CDP_STRINGS.cancel),
      deleteEventFeed: t(CDP_STRINGS.removeEventFeed),
      editSuccessMetricDescription: t(CDP_STRINGS.editSuccessMetricDescription),
      eventFeedLabel: t(CDP_STRINGS.eventFeedLabel),
      formErrorMessage: t(CDP_STRINGS.successMetricsFormErrorMessage),
      save: t(CDP_STRINGS.save),
      saveChanges: t(CDP_STRINGS.saveChanges),
      segment: t(CDP_STRINGS.segment),
      selectEventFeed: t(CDP_STRINGS.selectEventFeed),
      selectEventFeeds: t(CDP_STRINGS.selectEventFeeds),
      successMetric: t(CDP_STRINGS.successMetric),
      successMetricAddedDescriptionOne: t(CDP_STRINGS.successMetricAddedDescriptionOne),
      successMetricAddedDescriptionTwo: t(CDP_STRINGS.successMetricAddedDescriptionTwo),
      successMetricAddedTitle: t(CDP_STRINGS.successMetricAddedTitle),
      toastEventFailureAddedMessage: t(CDP_STRINGS.toastErrorTitle),
      toastEventFailureUpdatedMessage: t(CDP_STRINGS.toastErrorLabel),
      toastEventSuccessUpdatedTitle: t(CDP_STRINGS.toastEventSuccessUpdatedTitle),
    };
  }, [t]);

  const addBlankMetric = useCallback((): void => {
    setSelectedMetrics((current) => uniq([...current, null]));
  }, []);

  const removeMetric = useCallback(
    (index: number): void => {
      setSelectedMetrics((current) => {
        const nextMetrics = [...selectedMetrics];
        nextMetrics.splice(index, 1);
        return uniq(nextMetrics);
      });
    },
    [selectedMetrics],
  );

  const updateSelectedMetrics = useCallback(
    (index: number, value: string): void => {
      setSelectedMetrics((current) => {
        const nextMetrics = [...selectedMetrics];
        nextMetrics[index] = value;
        return uniq(nextMetrics);
      });
    },
    [selectedMetrics],
  );

  const saveSuccessMetric = useCallback(
    (segment: EntitySegment): void => {
      const errorToastProps: ToastProps = {
        showToast: true,
        stateHandler: useToastStore,
        toastContent: <strong>{labels.toastEventFailureAddedMessage}</strong>,
        toastType: TOAST_TYPE_DANGER,
      };

      const successToastProps: ToastProps = {
        showToast: true,
        stateHandler: useToastStore,
        title: labels.successMetricAddedTitle,
        toastContent: (
          <span>
            {labels.successMetricAddedDescriptionOne}
            <span style={{ fontStyle: `italic` }}>{selectedSegment?.name}</span>
            {labels.successMetricAddedDescriptionTwo}
          </span>
        ),
        toastType: TOAST_TYPE_SUCCESS,
      };

      if (
        property(`success`)(selectedSegment) &&
        Object.keys(property(`success`)(selectedSegment) as object).length >= 1
      ) {
        successToastProps.title = labels.toastEventSuccessUpdatedTitle;
        successToastProps.toastContent = <></>;

        errorToastProps.toastContent = <strong>{labels.toastEventFailureUpdatedMessage}</strong>;
      }

      lastValueFrom(
        segmentationService.updateSegment({
          segment,
          segmentId: selectedSegment?.internalId as string,
        }),
      )
        .then(() => {
          updateActiveToast(
            <Toast {...successToastProps} data-id={SEGMENTATION_DATA_IDS.saveSuccessMetricsToastSuccess} />,
          );
          close();
          return true;
        })
        .catch(() => {
          setSubmit(false);
          updateActiveToast(
            <Toast {...errorToastProps} data-id={SEGMENTATION_DATA_IDS.saveSuccessMetricsToastFailure} />,
          );
          return true;
        });
    },
    [
      close,
      labels.successMetricAddedDescriptionOne,
      labels.successMetricAddedDescriptionTwo,
      labels.successMetricAddedTitle,
      labels.toastEventFailureAddedMessage,
      labels.toastEventFailureUpdatedMessage,
      labels.toastEventSuccessUpdatedTitle,
      segmentationService,
      selectedSegment,
      updateActiveToast,
    ],
  );

  const customClose = useCallback(() => {
    resetSegments();
  }, [resetSegments]);

  const validateForm = useCallback(() => {
    const validMetrics = selectedMetrics.filter((metric) => metric !== null);
    if (validMetrics.length === 0) {
      setSubmit(false);
      setFormError(labels.formErrorMessage);
      return false;
    }

    setFormError('');
    return true;
  }, [labels.formErrorMessage, selectedMetrics, setFormError]);

  const submitHandler = useCallback(() => {
    if (!validateForm()) {
      return;
    }

    const updatedSegment = {
      ...selected,
      success: (selectedMetrics as Array<string>).reduce((acc: EntitySegment['success'], curr: string) => {
        if (acc && curr !== null) {
          acc[curr as string] = {
            metricid: null,
          };
        }
        return acc;
      }, {}),
    };

    // When success metric dialog forms are actioned in quick succession, the toast might get confused with another.
    // This is because we are using a single global toast (we do not want the toast to disappear when this dialog closes).
    updateActiveToast(null);
    saveSuccessMetric(updatedSegment as EntitySegment);
  }, [saveSuccessMetric, selected, selectedMetrics, updateActiveToast, validateForm]);

  useEffect(() => {
    const closeButton = (
      <Button
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => {
          close();
        }}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );
    const saveButton = (
      <Button
        data-id={SEGMENTATION_DATA_IDS.saveSuccessMetricsButton}
        isLoading={isFormSubmitted}
        label={isEditing ? labels.save : labels.add}
        onClick={(): void => {
          setSubmit(true);
          submitHandler();
        }}
        text={isEditing ? labels.save : labels.add}
      />
    );

    setAfterClose && setAfterClose(() => customClose);

    // Form submission check
    if (isFormSubmitted) {
      setButtonKeys([`save`]);
      setButtons([saveButton]);
    } else {
      setButtonKeys([`close`, `save`]);
      setButtons([closeButton, saveButton]);
    }
  }, [
    close,
    customClose,
    isEditing,
    isFormSubmitted,
    labels,
    setAfterClose,
    setButtonKeys,
    setButtons,
    setSubmit,
    submitHandler,
  ]);

  if (!eventList || !segmentList || !selected) {
    return <></>;
  }

  return (
    <div className="segment-item__success-metric-dialog-content">
      <SelectField
        defaultOption={
          selected && {
            key: selected.internalId as string,
            label: selected.name,
            value: selected.internalId as string,
          }
        }
        fieldLabel={labels.segment}
        id="success-metric-select-segment"
        name="success-metric-select-segment"
        options={segmentList.map((segment): SelectFieldOption => {
          return {
            key: segment.internalId as string,
            label: segment.name,
            value: segment.internalId as string,
          };
        })}
        readOnly
      />
      <div className="segment-item__success-metric-dialog-content-description">
        {isEditing ? labels.editSuccessMetricDescription : labels.addSuccessMetricDescription}
      </div>
      <div className="segment-item__success-metric-dialog-content-metrics">
        <h5 className="segment-item__success-metric-dialog-content-metrics-header">{labels.selectEventFeeds}</h5>
        <>
          {selectedMetrics.map((selectedMetric, index): JSX.Element => {
            const id = `${Math.random() * 1000}-${new Date().toISOString()}`;
            const name = property(`${selectedMetric}.name`)(eventList);
            const selectedEventOption: SelectFieldProps['defaultOption'] = {
              key: `${selectedMetric}`,
              label: isUndefined(name) ? `` : (name as string),
              value: `${selectedMetric}`,
            };
            return (
              <div
                className="segment-item__success-metric-dialog-content-metric"
                data-selected-metric={selectedMetric}
                id={`metric-${id}`}
                key={`metric-${id}`}
              >
                <SelectField
                  callback={(changed): void => {
                    updateSelectedMetrics(index, changed.value);
                  }}
                  defaultOption={selectedEventOption}
                  fieldLabel={labels.selectEventFeed}
                  hideLabel
                  id={`success-metric-select-event-${id}`}
                  name={`success-metric-select-event-${id}`}
                  options={Object.entries(eventList)
                    .filter(([eventId, event]) => {
                      return availableMetrics.includes(eventId);
                    })
                    .map(([eventId, event]): SelectFieldOption => {
                      return {
                        key: eventId as string,
                        label: event.name,
                        value: eventId as string,
                      };
                    })
                    .concat(selectedEventOption)}
                  placeholder={labels.selectEventFeed}
                  value={isNull(selectedMetric) ? undefined : selectedMetric}
                />
                <Button
                  data-id={SEGMENTATION_DATA_IDS.removeEventFeedButton}
                  format={BUTTON_FORMAT_TRANSPARENT}
                  icon={ICON_REMOVE_CIRCLE}
                  label={labels.deleteEventFeed}
                  onClick={(): void => {
                    removeMetric(index);
                  }}
                  theme={BUTTON_THEME_DEFAULT}
                />
              </div>
            );
          })}
        </>
      </div>
      <Button
        format={BUTTON_FORMAT_TRANSPARENT}
        icon={ICON_ADD_CIRCLE_OUTLINE}
        label={labels.addEventFeed}
        onClick={(): void => {
          addBlankMetric();
        }}
        text={labels.addEventFeed}
        theme={BUTTON_THEME_PRIMARY}
      />
      {formError && (
        <div className="sds-form-error" data-id={DXP_DATA_ID_FORM_ERROR}>
          <ErrorMessage message={formError} />
        </div>
      )}
    </div>
  );
};
